import { View, YStack, XStack, Text } from "tamagui";
import { colors } from "@styles/colors";
import { RiArrowLeftSLine } from "react-icons/ri";
import TextCustom, { TextSize } from "@components/TextCoustom";
import Steper from "@screen/Register/components/Steper";
import { useHazePage, v2 } from "@screen/Home/pages/GasShop/skin";

export const RADIUS = 16; // V2：卡片圓角統一放大（SkinCard 是 24，帶選取框的卡取中間值）
export const SHEET_RADIUS = 18;

export function Header({
  title,
  sub,
  step,
  total,
  onBack,
}: {
  title: string;
  sub?: string;
  step?: number;
  total?: number;
  onBack?: () => void;
  /** @deprecated V2 的標題列沒有自己的底色，色彩由整頁漸層負責 */
  color?: string;
}) {
  // 淡霧頁：整頁底色是極淡的藍白，這一組原本寫死的白字完全讀不到（見 useHazePage）
  const haze = useHazePage();

  /* Skin V2：標題列不再是「藍色圓角區塊」，而是直接坐在整頁漸層上的一段字
     （與 SkinV2Screen／PageWrapper 的釘住標題列同款：小字標題 + 一行說明，前景色跟著 haze 走）。
     也不再 sticky——.gas-skin-v2-topbar 的漸層以元素頂端為原點，跟著捲動就會與
     頁面背景錯開，浮出一道色差邊。這裡的步驟頁都很短，標題捲走沒有損失。 */
  return (
    <YStack px={16} pt={14} pb={20} width="100%">
      {/* 返回鍵單獨一列，標題不縮成小字：這條流程沒有釘住的標題列（PageWrapper 對這頁隱藏了
          自己的 header），大標得由這裡撐起來，否則只剩兩行差不多大的字，層級是平的。 */}
      <XStack
        ai="center"
        cursor="pointer"
        ml="$-1"
        mb="$2"
        onPress={onBack}
        opacity={onBack ? 1 : 0}
        pointerEvents={onBack ? "auto" : "none"}>
        <RiArrowLeftSLine size={20} color={haze ? v2.hazeInk : colors.light} />
        <TextCustom str="返回" type={TextSize.EXTRA_SMALL} color={haze ? v2.hazeInk : "rgba(255,255,255,0.9)"} bold />
      </XStack>
      <Text fontSize={22} fontWeight="800" color={haze ? v2.hazeInk : colors.light}>
        {title}
      </Text>
      {sub && (
        <Text fontSize={13} color={haze ? v2.hazeSub : "rgba(255,255,255,0.85)"} mt="$1.5">
          {sub}
        </Text>
      )}
      {step != null && total != null && (
        <XStack mt="$3">
          <Steper total={total} current={step} />
        </XStack>
      )}
    </YStack>
  );
}

export function StickyBtn({
  label,
  disabled,
  onClick,
  icon,
  color = colors.primary,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  icon?: any;
  color?: string;
  /** @deprecated V2 的主鈕一律實色膠囊，不再吃 hover 色 */
  hoverColor?: string;
  /**
   * @deprecated V2 的主鈕一律實色膠囊。
   * 這個 prop 收的是 CSS 漸層字串，塞進 backgroundColor 會是無效色＝整顆鈕透明（只剩白字）。
   */
  background?: string;
}) {
  /* V2：實色膠囊 + 由透明淡入頁面底色的襯底（.gas-skin-v2-bottom-fade）。
     原本的白色橫條會在捲動內容上切出一條硬邊；漸層方塊則會變成版面裡的第二個重心。 */
  return (
    <YStack className="gas-skin-v2-bottom-fade" pt="$4" style={{ position: "sticky", bottom: 0, width: "100%", zIndex: 100 }}>
      <XStack
        backgroundColor={disabled ? colors.gray100 : color}
        borderRadius={999}
        py={15}
        alignItems="center"
        justifyContent="center"
        opacity={disabled ? 0.6 : 1}
        cursor={disabled ? "default" : "pointer"}
        pressStyle={disabled ? undefined : { opacity: 0.85 }}
        onPress={() => !disabled && onClick()}>
        <XStack gap={10} ai="center">
          <Text color="white" fontSize={16} fontWeight="800">
            {label}
          </Text>
          {icon}
        </XStack>
      </XStack>
    </YStack>
  );
}

export const Fade = ({ children, i = 0 }: { children: React.ReactNode; i?: number }) => (
  <View animation="quick" enterStyle={{ opacity: 0, y: 8 }} opacity={1} y={0} style={{ transitionDelay: `${i * 30}ms` }}>
    {children}
  </View>
);

export function RadioCard({
  selected,
  onSelect,
  children,
  accent = colors.primary,
}: {
  selected: boolean;
  onSelect: () => void;
  children: React.ReactNode;
  accent?: string;
}) {
  return (
    <YStack
      onPress={onSelect}
      bg={colors.light}
      borderWidth={selected ? 2 : 1}
      borderColor={selected ? accent : "#EAEFF6"}
      br={RADIUS}
      p="$3.5"
      cursor="pointer"
      style={{ boxShadow: selected ? `0 4px 16px ${accent}1A` : "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
      animation="quick">
      <XStack ai="flex-start" gap="$3">
        <View
          w={18}
          h={18}
          br={9}
          borderWidth={selected ? 5 : 1.5}
          borderColor={selected ? accent : colors.gray100}
          bg={colors.light}
          mt={3}
          animation="quick"
        />
        <View f={1} minWidth={0}>
          {children}
        </View>
      </XStack>
    </YStack>
  );
}
